import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, Subscription, interval, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { getApiUrl } from '../helpers/rest-utils';
import { UserService } from '../user/user.service';
import { SessionService } from '../session.service';
import { Agent, CallRateLimit } from '../agent/agent';

/** A limit as the banner tracks it: the API retry-after turned into a wall-clock expiry. */
export interface ActiveCallRateLimit extends CallRateLimit {
  expiresAt: number;
}

export const CALL_RATE_LIMIT_POLL_INTERVAL_MS = 60 * 1000;

/**
 * Tracks the business's own numbers (test numbers, transfer destinations)
 * that the API is currently denying for too many test calls. The list comes
 * with every agent fetch and from a light poll while the dashboard is open,
 * so the banner shows up even when the owner only calls and watches.
 */
@Injectable({
  providedIn: 'root'
})
export class CallRateLimitService implements OnDestroy {
  private readonly limits = new BehaviorSubject<ActiveCallRateLimit[]>([]);
  private pollSubscription: Subscription;

  readonly limits$: Observable<ActiveCallRateLimit[]> = this.limits.asObservable();

  private readonly http = inject(HttpClient);
  private readonly user = inject(UserService);
  private readonly session = inject(SessionService);

  constructor() {
    this.session.terminate.subscribe(() => {
      this.stopPolling();
      this.limits.next([]);
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  get activeLimits(): ActiveCallRateLimit[] {
    const now = Date.now();
    return this.limits.getValue().filter(limit => limit.expiresAt > now);
  }

  get hasActiveLimits(): boolean {
    return this.activeLimits.length > 0;
  }

  /** Take the list embedded in an agent payload (every agent fetch refreshes it). */
  syncFromAgent(agent: Agent): void {
    if (!agent || !Array.isArray(agent.callRateLimits)) {
      return;
    }
    this.setLimits(agent.callRateLimits);
  }

  /** Fetch the current list from the API. Completes silently when no agent is loaded. */
  refresh(): Observable<ActiveCallRateLimit[]> {
    const agentId = this.user.userData?.agent?.id;
    const businessId = this.user.activeBusiness?.id;
    if (!agentId || !businessId) {
      return EMPTY;
    }
    return this.http.get<{ data?: CallRateLimit[] }>(`${getApiUrl()}agents/${agentId}/call-rate-limits`, { headers: { 'X-Business-ID': businessId } })
      .pipe(
        map(res => res?.data || []),
        catchError(() => of(null)),
        tap((limits: CallRateLimit[] | null) => {
          if (limits) {
            this.setLimits(limits);
          }
        }),
        map(() => this.activeLimits)
      );
  }

  startPolling(intervalMs: number = CALL_RATE_LIMIT_POLL_INTERVAL_MS): void {
    if (this.pollSubscription) {
      return;
    }
    this.pollSubscription = interval(intervalMs).pipe(switchMap(() => this.refresh())).subscribe();
  }

  stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = null;
    }
  }

  /** Drop entries whose window has ended; returns true when something changed. */
  pruneExpired(): boolean {
    const current = this.limits.getValue();
    const active = this.activeLimits;
    if (active.length === current.length) {
      return false;
    }
    this.limits.next(active);
    return true;
  }

  private setLimits(limits: CallRateLimit[]): void {
    const now = Date.now();
    this.limits.next(
      (limits || [])
        .filter(limit => limit && limit.number && limit.retryAfterSeconds > 0)
        .map(limit => ({ ...limit, expiresAt: now + limit.retryAfterSeconds * 1000 }))
    );
  }
}

/** "1 minute", "12 minutes", "23 hours" — what the banner shows for a retry-after. */
export function formatRetryAfter(expiresAt: number, now: number = Date.now()): { key: string; count: number } {
  const minutes = Math.max(1, Math.ceil((expiresAt - now) / 60000));
  if (minutes < 120) {
    return { key: minutes === 1 ? 'CALL_RATE_LIMIT.MINUTE' : 'CALL_RATE_LIMIT.MINUTES', count: minutes };
  }
  return { key: 'CALL_RATE_LIMIT.HOURS', count: Math.ceil(minutes / 60) };
}
